/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState } from 'react';
import { RequestStatus } from '../types/RequestStatus';
import * as api from '../api/makeRequest';
import * as logger from '../logging/logger';

export interface ApiRequestActions<T> {
    makeRequest: (method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, data?: any) => void;
    setData: (data: T) => void;
}

export interface UseApiRequestOptions<T> {
    onSuccess?: (method: 'GET' | 'POST' | 'PUT' | 'DELETE', response?: T) => void;
    onError?: (error: Error) => void;
}

interface ApiRequestMetadata {
    totalResults: number;
}

export function useApiRequest<T>(
    options?: UseApiRequestOptions<T>
): [T, ApiRequestActions<T>, RequestStatus, ApiRequestMetadata] {
    const [response, setResponse] = useState<T>(([] as unknown) as T);
    const [requestStatus, setRequestStatus] = useState<RequestStatus>({
        hasInitialResponse: false,
        isPending: false,
        hasError: false
    });
    const [metadata, setMetadata] = useState<ApiRequestMetadata>({ totalResults: 0 });

    const makeRequest = useCallback(
        async (method, path, data) => {
            try {
                setRequestStatus({ ...requestStatus, isPending: true, hasError: false });

                const response = await api.makeRequest({ method, path, data });

                if (response && response.payload) {
                    setMetadata({ totalResults: response.count });
                    setResponse(response.payload as T);
                } else {
                    setResponse(response as T);
                }

                setRequestStatus({ hasInitialResponse: true, isPending: false, hasError: false });
                options?.onSuccess &&
                    options.onSuccess(method, response && response.payload ? response.payload : response);
            } catch (error) {
                setRequestStatus({ ...requestStatus, isPending: false, hasError: true });
                logger.log('Debug', `ApiRequest: Error: ${error.message}`);
                options?.onError && options.onError(error);
            }
        },
        [requestStatus, options]
    );

    return [response, { makeRequest, setData: setResponse }, requestStatus, metadata];
}
